<template>
  <div>
    <!-- Top navbar -->
    <nav class="navbar navbar-top navbar-expand-md bg-white shadow border-bottom" id="navbar-main">
      <div class="container-fluid">
        <!-- Brand -->
        <router-link :to="{ name: 'completedSession' }" class="h4 mb-0 text-uppercase d-none d-lg-inline-block">
          Closed Session
        </router-link>
        <user-data></user-data>

      </div>
    </nav>
    <!-- List view content -->
    <div class="container-fluid mt-0 pt-5 pt-md-8">
      <div class="row mb-5 mb-xl-0">
        <div class="col-md-3">
          <b-form-group label="Site" label-for="session">
            <b-form-select id="session" @input="getCompletedList(form), getMeterList()" v-model="form.site_id"
              class="form-control-alternative">
              <option value="null" class="bg-grey text-white" disabled selected>Please select Site</option>
              <option v-for="list in coPropList.data" :key="list.id" v-bind:value='list.id'>{{ list.name }}</option>
            </b-form-select>
          </b-form-group>
        </div>
        <div class="col-md-3">
          <b-form-group label="Meter Type" label-for="session">
            <b-form-select id="session" @input="getCompletedList(form)" v-model="form.service_type_id"
              class="form-control-alternative" :disabled="form.site_id == null">
              <option value='null' class="bg-grey text-white" disabled selected>Please select Meter</option>
              <option v-for="list in meterList" :key="list.id" v-bind:value='list.id'>{{ list.service_name }}</option>
            </b-form-select>
          </b-form-group>
        </div>
        <div class="col-md-3">
          <b-form-group id="formDate" label="From Date" label-for="formDate">
            <date-picker v-model="form.dateFrom" :config="dateFormat" placeholder="Please select from date"
              class="form-control-alternative" @input="modifyPage(form)" :disabled="form.site_id == null"></date-picker>
          </b-form-group>
        </div>
        <div class="col-md-3">
          <b-form-group id="toDate" label="To Date" label-for="toDate">
            <date-picker v-model="form.dateTo" :config="dateFormat" placeholder="Please select to date"
              class="form-control-alternative" @input="modifyPage(form)"
              :disabled="form.site_id == null || form.dateFrom == ''"></date-picker>
          </b-form-group>
        </div>
      </div>
      <div class="row mt-4" v-if="form.site_id != null">
        <div class="col-md-12 mb-5 mb-xl-0">
          <p v-if="rollbackBlockedByOpenSession" class="alert text-dark alert-info mb-3">
            <i class="fas fa-info-circle mr-1"></i>
            Session rollback disabled as there is an active open session
          </p>
          <div class="card shadow">
            <div class="card-header border-0">
              <div class="row align-items-center">
                <div class="col">
                  <h3 class="mb-0">Closed Session Data</h3>
                </div>
              </div>
            </div>
            <div class="table-responsive" v-if="completedSessionData.total_count != 0">
              <b-table class="mt-3" striped hover :items="completedSessionList" :fields="listFields">
                <template slot="index" slot-scope="data">
                  {{ data.index + 1 }}
                </template>
                <template slot="coProp" slot-scope="data">
                  {{ data.item.site.name }}
                </template>
                <template slot="meterType" slot-scope="data">
                  {{ data.item.service_type.service_name }}
                </template>
                <template slot="created_at" slot-scope="data">
                  <span :class="{ 'void-text': data.item.is_voided }">{{ fmtDateTime(data.item.survey_end_date) }}</span>
                  <b-badge v-if="data.item.is_voided" variant="danger" class="ml-1"
                    v-b-tooltip.hover="voidTooltip(data.item)">VOID</b-badge>
                </template>
                <template slot="total_lot_count" slot-scope="data">
                  {{ data.value }}
                </template>
                <template slot="total_load_units" slot-scope="data">
                  {{ data.item.total_load_units | formatNumber }}
                </template>
                <template slot="total_load_charge" slot-scope="data">
                  {{ data.item.total_load_charge | currency }}
                </template>
                <template slot="vilogi" slot-scope="data">
                  <!-- Void sessions never sync: show a neutral badge instead of the live status (which can read 'syncing' while entries are cancelled/reversed). 2026-10-20 -->
                  <b-badge v-if="data.item.is_voided && isVilogiReversalLeftover(data.item)" variant="warning" class="px-2 py-1 vilogi-badge-lg"
                    v-b-tooltip.hover="'Session voided, but some Vilogi entries are still posted. Open the Vilogi menu to retry the reversal.'">
                    Reversal pending
                  </b-badge>
                  <b-badge v-else-if="data.item.is_voided" variant="secondary" class="px-2 py-1 vilogi-badge-lg"
                    v-b-tooltip.hover="'Session voided — Vilogi entries are cancelled or reversed (status: ' + vilogiStatusLabel(data.item.vilogi_status) + ')'">
                    Voided
                  </b-badge>
                  <b-badge v-else :variant="vilogiStatusVariant(data.item.vilogi_status)" class="px-2 py-1 vilogi-badge-lg">
                    <i v-if="data.item.vilogi_status === 'syncing'" class="fas fa-spinner fa-spin mr-1"></i>
                    {{ vilogiStatusLabel(data.item.vilogi_status) }}
                  </b-badge>
                </template>
                <template slot="actions" slot-scope="data">
                  <div class="d-flex w-100 justify-content-between font-size">
                    <b-link v-b-tooltip.hover="'View all readings'" @click='goToSessionDetail(data.item)'
                      class="text-info"><i class="far fa-eye mr-2"></i></b-link>
                    <!-- Single Download Button (kept for void sessions too) -->
                    <b-link v-b-tooltip.hover="'Download readings'"
                      @click="showDownloadModal(data.item.id)" class="text-success">
                      <i class="fas fa-download mr-2"></i>
                    </b-link>

                    <!-- Roll back (void) button -->
                    <b-link v-if="data.item.canRollback" v-b-tooltip.hover="'Roll back session'"
                      @click="showRollbackModal(data.item)" class="text-danger">
                      <i class="fas fa-undo mr-2"></i>
                    </b-link>

                    <!-- Single Email Button -->
                    <b-link v-if="data.item.is_voided" class="d-none"></b-link>
                    <b-link v-else-if="data.item.is_mail_scheduled == 1" v-b-tooltip.hover="'Email'"
                      @click="showEmailModal(data.item.id)" class="text-primary">
                      <i class="fas fa-envelope mr-2"></i>
                    </b-link>


                    <!-- Single Email Button -->
                    <b-link v-else v-b-tooltip.hover="'Email'" @click="showEmailModal(data.item.id)"
                      class="text-danger">
                      <i class="fas fa-envelope mr-2"></i>
                    </b-link>

                    <!-- Vilogi Sync Button -->
                    <b-link v-if="canOpenVilogiModal(data.item)"
                      v-b-tooltip.hover="'Vilogi Sync'"
                      @click="showVilogiModal(data.item.id)" class="text-warning">
                      <i class="fas fa-sync-alt mr-2"></i>
                    </b-link>


                    <!-- Popup for download options -->
                    <b-modal :ref="'download-modal-' + data.item.id" ok-only ok-title="Close"
                      title="Choose Download Option">
                      <div class="alert alert-primary" role="alert">
                        Microsoft Excel format. <a
                          :href="apiUrl + 'site-service-csv?' + 'site_id=' + data.item.site.id + '&service_type_id=' + data.item.service_type.id + '&created_at=' + data.item.created_at + '&sp=' + data.item.service_provider_id + '&uuid=' + data.item.uuid"
                          class="alert-link">Download</a>.
                      </div>
                      <div v-if="!data.item.is_voided" class="alert alert-info" role="alert">
                        Bills in pdf format <a
                          :href="apiUrl + 'site-service-pdf?' + 'site_id=' + data.item.site.id + '&service_type_id=' + data.item.service_type.id + '&created_at=' + data.item.created_at + '&sp=' + data.item.service_provider_id + '&uuid=' + data.item.uuid"
                          class="alert-link">Download</a>.
                      </div>
                      <div v-if="!data.item.is_voided" class="alert alert-success" role="alert">
                        Notice of use in pdf format <a
                          :href="apiUrl + 'site-service-pdf?' + 'site_id=' + data.item.site.id + '&service_type_id=' + data.item.service_type.id + '&created_at=' + data.item.created_at + '&sp=' + data.item.service_provider_id + '&uuid=' + data.item.uuid + '&view=notify'"
                          class="alert-link">Download</a>.
                      </div>
                      <div v-if="!data.item.is_voided" class="alert alert-warning" role="alert">
                        Text file format <a
                          :href="apiUrl + 'site-service-txt?' + 'site_id=' + data.item.site.id + '&sp=' + data.item.service_provider_id + '&uuid=' + data.item.uuid"
                          class="alert-link">Download</a>.
                      </div>
                    </b-modal>

                    <!-- Popup for email -->
                    <b-modal ok-only ok-title="Close" :ref="'email-modal-' + data.item.id" title="Schedule email">

                      <div class="alert alert-primary" role="alert" v-if="data.item.is_mail_scheduled == 1">
                        <i class="far fa-check mr-2"></i> Email has been scheduled for this session.
                      </div>

                      <div v-else>
                        <h3>Choose an email type</h3>
                        <div class="d-flex justify-content-around mt-3">
                          <b-button variant="primary" @click='scheduleMail(data.item, "mail")'>Bill</b-button>
                          <b-button variant="secondary" @click='scheduleMail(data.item, "another")'>Notice of
                            Consumption</b-button>
                        </div>
                      </div>

                    </b-modal>

                    <!-- Popup for Vilogi sync -->
                    <b-modal ok-only ok-title="Close" :ref="'vilogi-modal-' + data.item.id" title="Vilogi Accounting Sync">
                      <!-- The modal renders inside the table cell, which inherits nowrap; reset it so text wraps within the modal. 2026-10-20 -->
                      <div class="vilogi-modal-body">
                      <p class="text-muted mb-3">
                        <span v-if="data.item.vilogi_status === 'not_scheduled'">This session hasn't been sent to Vilogi yet.</span>
                        <span v-else-if="data.item.vilogi_status === 'cancelled'">This session's Vilogi sync was cancelled before any bill was sent. Posting again will pick it back up.</span>
                        <span v-else-if="data.item.vilogi_status === 'syncing'">This session is currently syncing to Vilogi — this is usually quick.</span>
                        <span v-else-if="data.item.is_voided">This session is voided, but some of its entries are still posted in Vilogi. Retry the reversal to clear them. Voided sessions are never posted again.</span>
                        <span v-else-if="data.item.vilogi_status === 'synced'">This session is posted to Vilogi. Reversing removes its entries from Vilogi; bills, readings and emails are not affected.</span>
                        <span v-else-if="data.item.vilogi_status === 'reversed'">This session's Vilogi postings were reversed. Post to Vilogi to send them again.</span>
                        <span v-else-if="data.item.vilogi_status === 'error'">At least one bill in this session failed to sync. Retry here only catches lots that haven't been sent yet — for the failed one(s), open this session's lot details and retry them individually.</span>
                      </p>
                      <div class="d-flex justify-content-around mt-3">
                        <b-button v-if="canPostToVilogi(data.item)" variant="primary"
                          :disabled="vilogiActionLoading" @click="triggerVilogi(data.item)">
                          <b-spinner v-if="vilogiActionLoading" small></b-spinner>
                          <span v-else>{{ data.item.vilogi_status === 'error' ? 'Retry' : 'Post to Vilogi' }}</span>
                        </b-button>
                        <b-button v-if="!data.item.is_voided && data.item.vilogi_status === 'syncing'" variant="outline-danger"
                          :disabled="vilogiActionLoading" @click="cancelVilogi(data.item)">
                          <b-spinner v-if="vilogiActionLoading" small></b-spinner>
                          <span v-else>Cancel Pending</span>
                        </b-button>
                        <b-button v-if="canReverseVilogi(data.item)" variant="outline-danger"
                          :disabled="vilogiActionLoading" @click="reverseVilogi(data.item)">
                          <b-spinner v-if="vilogiActionLoading" small></b-spinner>
                          <span v-else>{{ data.item.is_voided ? 'Retry Vilogi reversal' : 'Reverse Vilogi postings' }}</span>
                        </b-button>
                      </div>
                      <div v-if="vilogiUnreversibleLots[data.item.id] && vilogiUnreversibleLots[data.item.id].length"
                        class="alert alert-warning mt-3 mb-0 text-left vilogi-blocked-alert">
                        <p class="font-weight-bold mb-2">
                          <i class="fas fa-exclamation-triangle mr-1"></i>
                          These lots were posted before automatic reversal was supported and must be reversed manually in Vilogi:
                        </p>
                        <ul class="mb-0 pl-3 small">
                          <li v-for="lot in vilogiUnreversibleLots[data.item.id]" :key="lot">{{ lot }}</li>
                        </ul>
                      </div>
                      <div v-if="vilogiReverseError[data.item.id]" class="alert alert-danger mt-3 mb-0 text-left vilogi-blocked-alert">
                        {{ vilogiReverseError[data.item.id] }}
                      </div>
                      <div v-if="vilogiBlockedLots[data.item.id] && vilogiBlockedLots[data.item.id].length"
                        class="alert alert-danger mt-3 mb-0 text-left vilogi-blocked-alert">
                        <p class="font-weight-bold mb-2">
                          <i class="fas fa-exclamation-triangle mr-1"></i>
                          {{ vilogiBlockedLots[data.item.id].length }} lot(s) have invalid Vilogi account information. Please correct these in the Address Book.
                        </p>
                        <ul class="mb-2 pl-3 small">
                          <li v-for="lot in vilogiBlockedLots[data.item.id]" :key="lot.archived_session_id">{{ lot.lot_no }}</li>
                        </ul>
                        <!-- <p class="small mb-0 text-muted">
                          Run <strong>Sync Vilogi Adherents</strong> on this site's edit page, then trigger the
                          whole batch again — a partial retry isn't supported.
                        </p> -->
                      </div>
                      </div>
                    </b-modal>

                  </div>
                </template>
              </b-table>
              <div class="card-footer border-0">
                <b-pagination align="right" @input="pageClick(currentPage)"
                  :total-rows="completedSessionData.total_count" v-model="currentPage" :per-page="10">
                </b-pagination>
              </div>
            </div>
            <div v-if="completedSessionData.total_count == 0">
              <h5 class="text-center py-5">No records available</h5>
            </div>
          </div>
        </div>
      </div>
      <div class="row my-4" v-if="form.site_id == null">
        <div class="col-md-12 mb-5 mb-xl-0">
          <div class="card shadow mb-5">
            <div class="row m-0 px-2 pb-4">
              <div class="col-md-12">
                <h4 class="py-5 my-3 text-center"> Please select co props</h4>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Follow-up prompt after a bill email is scheduled -->
    <vilogi-prompt-modal ref="vilogiPrompt" @posted="getCompletedList(form)"></vilogi-prompt-modal>

    <!-- Roll back confirmation modal -->
    <b-modal ref="rollbackModal" title="Roll back session" centered no-close-on-backdrop no-close-on-esc hide-footer>
      <div v-if="rollbackTarget">
        <div class="alert alert-warning">
          <i class="fas fa-exclamation-triangle mr-2"></i>
          Rolling back will <strong>void</strong> every bill in this session and restore the lots' previous
          opening readings. Customers who already received their bill will be emailed a void notice with the
          cancelled invoice attached.
        </div>
        <p class="mb-2">
          <strong>{{ rollbackTarget.site.name }}</strong> &middot; {{ rollbackTarget.service_type.service_name }}
          &middot; closed {{ fmtDateTime(rollbackTarget.survey_end_date) }}
        </p>
        <b-form-group label="Reason (optional)" label-for="rollbackReason"
          description="Shown in the void email. Leave empty for &quot;Administrative correction&quot;.">
          <b-form-textarea id="rollbackReason" v-model="rollbackReason" maxlength="255" rows="3"
            :disabled="rollbackLoading"></b-form-textarea>
        </b-form-group>
        <div v-if="rollbackError" class="alert alert-danger">
          <p class="mb-1">{{ rollbackError }}</p>
          <ul v-if="rollbackLots.length" class="mb-0 pl-3 small">
            <li v-for="lot in rollbackLots" :key="lot">{{ lot }}</li>
          </ul>
        </div>
        <div class="d-flex justify-content-end">
          <b-button variant="secondary" class="mr-2" :disabled="rollbackLoading"
            @click="$refs.rollbackModal.hide()">Cancel</b-button>
          <b-button variant="danger" :disabled="rollbackLoading" @click="submitRollback">
            <b-spinner v-if="rollbackLoading" small></b-spinner>
            <span v-else>Roll back session</span>
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Pre-mailing check modal -->
    <b-modal ref="mailCheckModal" title="Email Check" hide-footer no-close-on-backdrop no-close-on-esc centered>
      <!-- Checking state -->
      <div v-if="mailCheckLoading" class="text-center py-4">
        <b-spinner variant="primary" class="mb-3"></b-spinner>
        <p class="mb-0 text-muted">Checking for invalid email addresses...</p>
      </div>

      <!-- All clear -->
      <div v-else-if="mailCheckFailures.length === 0" class="text-center py-4">
        <i class="fas fa-check-circle text-success mb-3" style="font-size:40px;"></i>
        <p class="mb-0 text-success font-weight-bold">No invalid addresses found</p>
        <p class="text-muted small mt-1">Scheduling mailing...</p>
      </div>

      <!-- Failures found -->
      <div v-else>
        <div class="alert alert-warning mb-3">
          <i class="fas fa-exclamation-triangle mr-2"></i>
          <strong>{{ mailCheckFailures.length }} failed address{{ mailCheckFailures.length > 1 ? 'es' : '' }} from the last mailing</strong>
        </div>
        <ul class="list-unstyled mb-4">
          <li v-for="item in mailCheckFailures" :key="item.lot_no" class="mb-2 p-2 border rounded">
            <span class="font-weight-bold text-danger mr-2">{{ item.lot_no }}</span>
            <span class="text-muted small">{{ item.reason }}</span>
          </li>
        </ul>
        <div class="d-flex justify-content-end">
          <b-button variant="secondary" class="mr-2" @click="$refs.mailCheckModal.hide()">
            Cancel &amp; Correct
          </b-button>
          <b-button variant="warning" @click="proceedWithMailing()">
            Ignore &amp; Continue
          </b-button>
        </div>
      </div>
    </b-modal>

  </div>
</template>
<script>
import { mapGetters, mapActions } from 'vuex';
import dayjs from 'dayjs';
import datePicker from 'vue-bootstrap-datetimepicker';
import { getPreferences, unitLabel } from '@/utils/preferences';
import { formatDate, formatDateTime, toUtcDateString } from '@/utils/dateFormat';
import 'pc-bootstrap4-datetimepicker/build/css/bootstrap-datetimepicker.css';
import userData from '@/pages/Common/userData';
import vilogiPromptModal from '@/pages/Common/vilogiPromptModal';
import { canOfferVilogi } from '@/utils/vilogiPrompt';
import ApiServices from '@/services/api';
import { apiUrl } from '@/config'
export default {
  components: {
    userData,
    vilogiPromptModal,
    datePicker,
    type: 'mail', // or default value
  },
  data() {
    return {
      apiUrl,
      currentPage: 1,
      mailCheckLoading: false,
      mailCheckFailures: [],
      pendingMailPayload: null,
      pendingMailFlag: null,
      pendingMailRow: null,
      vilogiActionLoading: false,
      vilogiBlockedLots: {},
      vilogiUnreversibleLots: {},
      vilogiReverseError: {},
      vilogiRefreshTimers: [],
      rollbackTarget: null,
      rollbackReason: '',
      rollbackLoading: false,
      rollbackError: '',
      rollbackLots: [],
      dateFormat: {
        format: 'YYYY-MM-DD',
        useCurrent: false,
        showClear: true
      },
      form: {
        site_id: null,
        service_type_id: null,
        dateFrom: '',
        dateTo: '',
        page_no: 1,
      },
      completedSessionList: [],
    }
  },
  computed: {
    ...mapGetters('coprops', ['coPropList', 'meterList']),
    ...mapGetters('sessions', ['completedSessionData', 'openSessionList']),
    prefs() {
      return getPreferences()
    },
    // True when a reading round is open for the selected site (and meter, if chosen) and there are live sessions that would otherwise be rollback candidates. 2026-10-20
    rollbackBlockedByOpenSession() {
      if (!this.form.site_id || !Array.isArray(this.openSessionList)) return false
      const hasOpenRound = this.openSessionList.some(s =>
        s.site_id == this.form.site_id &&
        (!this.form.service_type_id || s.service_type_id == this.form.service_type_id))
      return hasOpenRound && this.completedSessionList.some(row => !row.is_voided)
    },
    vilogiEnabled() {
      return !!(this.completedSessionData && this.completedSessionData.vilogi_enabled)
    },
    listFields() {
      const typeId = this.form ? Number(this.form.service_type_id) : 0
      const unitSuffix = typeId ? unitLabel(typeId, this.prefs) : 'units'
      const fields = [
        'index',
        { key: 'coProp',            label: 'Site',                     sortable: true,  class: 'text-center' },
        { key: 'meterType',         label: 'Meter Type',               sortable: true,  class: 'text-center' },
        { key: 'created_at',        label: 'Closed Date',              sortable: true,  class: 'text-center' },
        { key: 'total_lot_count',   label: 'Total Documents',          sortable: true,  class: 'text-center' },
        { key: 'total_load_units',  label: `Total ${unitSuffix}`,      sortable: true,  class: 'text-center' },
        { key: 'total_load_charge', label: 'Load Charge',              sortable: true,  class: 'text-center' }
      ]
      if (this.vilogiEnabled) {
        fields.push({ key: 'vilogi', label: 'Vilogi', sortable: false, class: 'text-center' })
      }
      fields.push({ key: 'actions', label: 'Actions', sortable: false, class: 'text-center' })
      return fields
    }
  },
  methods: {
    ...mapActions('coprops', ['ALL_CO_PROP_LIST', 'ALL_METER_LIST']),
    ...mapActions('sessions', ['OPEN_SESSION_LIST', 'COMPLETED_SESSION_LIST', 'COMPLETED_SESSION_MAIL', 'COMPLETED_SESSION_MAIL_ANOTHER', 'CRON_ANOTHER']),
    ...mapActions('mailing', ['CHECK_FAILED_MAILINGS']),
    fmtDate(d) {
      return formatDate(d)
    },
    fmtDateTime(d) {
      return formatDateTime(d)
    },
    getCoPropList() {
      // Get all site list
      return this.ALL_CO_PROP_LIST().then(() => {
      }).catch((e) => {
      });
    },
    getMeterList() {
      if (!this.form.site_id) {
        return Promise.resolve();
      }
      let meterListDetails = {
        site_id: this.form.site_id
      }
      return this.ALL_METER_LIST(meterListDetails).then(() => {
      }).catch((e) => {
        console.log(e);
      })
    },
    modifyPage(data) {
      data.page_no = 1
      this.form.page_no = 1
      this.getCompletedList(data)
    },
    getCompletedList(data) {
      if (data.dateTo == '' && data.dateFrom !== '') {
        data.dateTo = dayjs().format('YYYY-MM-DD')
      }
      if (data.dateTo != '') {
        if (data.dateTo < data.dateFrom) {
          this.ERROR_TOAST_MSG('Please select to date greater than from date');
        } else {
          data.dateFrom = toUtcDateString(data.dateFrom)
          data.dateTo   = toUtcDateString(data.dateTo, null, true)
        }
      }

      return this.COMPLETED_SESSION_LIST(data).then(() => {
        // Void sessions stay in the list; _rowVariant greys the row out. 2026-10-19
        this.completedSessionList = this.completedSessionData.data.data.map(row =>
          row.is_voided ? Object.assign({}, row, { _rowVariant: 'secondary' }) : row
        )
      }).catch(() => {

      });
    },
    async applyRoutePrefill() {
      const routePayload = this.$route.query.prefill
      if (!routePayload) {
        return
      }

      try {
        const session = JSON.parse(atob(routePayload))
        this.form.site_id = session.site_id
        await this.getMeterList()
        this.form.service_type_id = session.service_type_id
        await this.getCompletedList(this.form)
      } catch (e) { }
    },
    goToSessionDetail(sessionRow) {
      this.$router.push({
        name: 'completedSessionView',
        query: {
          view: btoa(JSON.stringify({
            site: { id: sessionRow.site.id, name: sessionRow.site.name },
            service_type: { id: sessionRow.service_type.id, service_name: sessionRow.service_type.service_name },
            uuid: sessionRow.uuid,
            created_at: sessionRow.created_at
          }))
        }
      })
    },
    showDownloadModal(id) {
      this.$refs['download-modal-' + id].show()
    },
    showEmailModal(id) {
      this.$refs['email-modal-' + id].show()
    },
    showVilogiModal(id) {
      this.$set(this.vilogiBlockedLots, id, [])
      this.$set(this.vilogiUnreversibleLots, id, [])
      this.$set(this.vilogiReverseError, id, '')
      this.$refs['vilogi-modal-' + id].show()
    },
    // True for a void session whose Vilogi entries are still posted/syncing (a rollback's reversal failed or is running). 2026-10-20
    isVilogiReversalLeftover(row) {
      return !!row.is_voided && ['synced', 'syncing'].includes(row.vilogi_status)
    },
    // Whether the Vilogi sync icon/modal is available; void sessions only get it to retry a leftover reversal. 2026-10-20
    canOpenVilogiModal(row) {
      if (!this.vilogiEnabled || row.vilogi_status === 'not_configured') return false
      return row.is_voided ? this.isVilogiReversalLeftover(row) : true
    },
    // Post is never offered for void sessions, nor while syncing/already synced. 2026-10-20
    canPostToVilogi(row) {
      return !row.is_voided && !['syncing', 'synced'].includes(row.vilogi_status)
    },
    canReverseVilogi(row) {
      if (row.is_voided) return this.isVilogiReversalLeftover(row)
      return ['synced', 'error', 'syncing'].includes(row.vilogi_status)
    },
    vilogiStatusVariant(status) {
      if (status === 'synced') return 'success'
      if (status === 'error') return 'danger'
      if (status === 'syncing') return 'info'
      if (status === 'cancelled') return 'secondary'
      if (status === 'reversed') return 'dark'
      if (status === 'not_scheduled') return 'warning'
      return 'light'
    },
    vilogiStatusLabel(status) {
      if (status === 'synced') return 'Synced'
      if (status === 'error') return 'Error'
      if (status === 'syncing') return 'Syncing'
      if (status === 'cancelled') return 'Cancelled'
      if (status === 'reversed') return 'Reversed'
      if (status === 'not_scheduled') return 'Not Scheduled'
      return 'Not Configured'
    },
    triggerVilogi(sessionRow) {
      if (!window.confirm('Post every eligible bill in this closed session to Vilogi? This will be executed immediately.')) return
      this.vilogiActionLoading = true
      ApiServices.triggerVilogiBatch({ uuid: sessionRow.uuid })
        .then(response => {
          const result = (response && response.data) || {}
          this.$set(this.vilogiBlockedLots, sessionRow.id, [])
          this.SUCCESS_TOAST_MSG(`Queued ${result.queued || 0}, skipped ${result.skipped || 0}`)
          this.$refs['vilogi-modal-' + sessionRow.id].hide()
          this.getCompletedList(this.form)
        })
        .catch(err => {
          // Global interceptor already toasts err.message. This specific rejection also
          // carries which lots are blocking the batch — surface those in the modal instead
          // of letting them get lost in a toast, and keep the modal open so the admin can
          // see them without re-opening it. 2026-09-28
          const lots = err && err.data && err.data.non_compliant_lots
          if (lots && lots.length) {
            this.$set(this.vilogiBlockedLots, sessionRow.id, lots)
          }
        })
        .finally(() => {
          this.vilogiActionLoading = false
        })
    },
    cancelVilogi(sessionRow) {
      if (!window.confirm('Cancel every still-pending Vilogi sync for this session? Entries already sent to Vilogi cannot be cancelled.')) return
      this.vilogiActionLoading = true
      ApiServices.cancelVilogiBatch({ uuid: sessionRow.uuid })
        .then(response => {
          const result = (response && response.data) || {}
          this.SUCCESS_TOAST_MSG(`Cancelled ${result.cancelled || 0}`)
          this.$refs['vilogi-modal-' + sessionRow.id].hide()
          this.getCompletedList(this.form)
        })
        .catch(() => {})
        .finally(() => {
          this.vilogiActionLoading = false
        })
    },
    // Reverses all of a session's Vilogi postings (cancelling pending ones), then refreshes the list a few seconds later and again after a minute. 2026-10-20
    reverseVilogi(sessionRow) {
      if (!window.confirm("This removes this session's entries from Vilogi and cancels any not yet sent. Bills, readings and emails are not affected.")) return
      this.vilogiActionLoading = true
      this.$set(this.vilogiReverseError, sessionRow.id, '')
      this.$set(this.vilogiUnreversibleLots, sessionRow.id, [])
      ApiServices.reverseVilogiBatch({ uuid: sessionRow.uuid })
        .then(response => {
          const result = (response && response.data) || {}
          const queued = result.queued || 0
          const cancelled = result.cancelled || 0
          const unreversible = result.unreversible_lots || []
          if (!queued && !cancelled) {
            this.SUCCESS_TOAST_MSG('Nothing to reverse.')
          } else {
            this.SUCCESS_TOAST_MSG(`${queued} entries are being reversed, ${cancelled} cancelled.`)
          }
          if (unreversible.length) {
            // Persistent warning: keep the modal open so the lots stay visible.
            this.$set(this.vilogiUnreversibleLots, sessionRow.id, unreversible)
          } else {
            this.$refs['vilogi-modal-' + sessionRow.id].hide()
          }
          this.getCompletedList(this.form)
          this.scheduleVilogiRefresh()
        })
        .catch(err => {
          // 404 isn't toasted by the interceptor, so always show the message inline.
          this.$set(this.vilogiReverseError, sessionRow.id, (err && err.message) || 'Vilogi reversal failed.')
        })
        .finally(() => {
          this.vilogiActionLoading = false
        })
    },
    // Refetches the list ~8s after a reversal, and again after a minute if any session still reads synced/syncing. 2026-10-20
    scheduleVilogiRefresh() {
      this.vilogiRefreshTimers.forEach(clearTimeout)
      this.vilogiRefreshTimers = [
        setTimeout(() => this.getCompletedList(this.form), 8000),
        setTimeout(() => {
          if (this.completedSessionList.some(r => ['synced', 'syncing'].includes(r.vilogi_status))) {
            this.getCompletedList(this.form)
          }
        }, 60000)
      ]
    },
    voidTooltip(row) {
      const when = row.voided_at ? this.fmtDateTime(row.voided_at) : ''
      return 'Rolled back' + (when ? ' on ' + when : '') + (row.voided_reason ? ': ' + row.voided_reason : '')
    },
    // Opens the roll back confirmation modal for a session row. 2026-10-19
    showRollbackModal(sessionRow) {
      this.rollbackTarget = sessionRow
      this.rollbackReason = ''
      this.rollbackError = ''
      this.rollbackLots = []
      this.$refs.rollbackModal.show()
    },
    // Calls the rollback endpoint; on failure keeps the modal open with the server message and lots, then refetches the list so stale canRollback flags correct themselves. 2026-10-19
    submitRollback() {
      this.rollbackLoading = true
      this.rollbackError = ''
      this.rollbackLots = []
      const reason = this.rollbackReason.trim()
      ApiServices.rollbackSession({ uuid: this.rollbackTarget.uuid, reason: reason || undefined })
        .then(response => {
          this.SUCCESS_TOAST_MSG((response && response.message) || 'Session rolled back successfully.')
          this.$refs.rollbackModal.hide()
          this.getCompletedList(this.form)
        })
        .catch(err => {
          // The interceptor toasts 422/400 but not 404, and masks 500 as "Network problem",
          // so the message is always shown inline here.
          this.rollbackError = (err && err.message) || 'Session rollback failed.'
          this.rollbackLots = (err && err.lots) || []
          this.getCompletedList(this.form)
        })
        .finally(() => {
          this.rollbackLoading = false
        })
    },
    hideDownloadModal() {
      this.$refs['download-modal'].hide()
    },
    toggleDownloadModal() {
      // We pass the ID of the button that we want to return focus to
      // when the modal has hidden
      this.$refs['download-modal'].toggle('#toggle-btn')
    },
    pageClick(pageNo) {
      this.form.page_no = pageNo
      this.getCompletedList(this.form)
    },
    scheduleMail(sessionRow, type) {
      this.pendingMailPayload = {
        site_id: sessionRow.site.id,
        uuid: sessionRow.uuid,
        service_type_id: sessionRow.service_type.id,
        created_at: dayjs(sessionRow.created_at).format('YYYY-MM-DD'),
        type: type == 'mail' ? 'bill' : 'notify'
      }
      this.pendingMailFlag = type
      this.pendingMailRow = sessionRow
      this.mailCheckFailures = []
      this.mailCheckLoading = true
      this.$refs.mailCheckModal.show()
      this.CHECK_FAILED_MAILINGS({ site_id: sessionRow.site.id })
        .then(response => {
          this.mailCheckLoading = false
          this.mailCheckFailures = response.data || []
          if (this.mailCheckFailures.length === 0) {
            setTimeout(() => {
              this.$refs.mailCheckModal.hide()
              this.proceedWithMailing()
            }, 1200)
          }
        })
        .catch(() => {
          this.mailCheckLoading = false
          this.$refs.mailCheckModal.hide()
          this.proceedWithMailing()
        })
    },
    triggerCronAnother() {
      this.CRON_ANOTHER().then(() => {
        this.getCompletedList(this.form)
      })
    },
    proceedWithMailing() {
      this.$refs.mailCheckModal.hide()
      const offerVilogi = this.pendingMailFlag === 'mail' && canOfferVilogi(this.pendingMailRow, this.vilogiEnabled)
      const row = this.pendingMailRow
      this.COMPLETED_SESSION_MAIL(this.pendingMailPayload).then(() => {
        this.getCompletedList(this.form)
        this.pendingMailPayload = null
        this.pendingMailFlag = null
        this.pendingMailRow = null
        // Bill emails (not notices) offer a follow-up Vilogi sync. 2026-10-20
        if (offerVilogi) this.$refs.vilogiPrompt.show(row)
      })
    },
  },
  created() {
    document.body.classList.remove('modal-open');

    if (!localStorage.token) {
      localStorage.clear()
      next({
        name: 'adminLogin'
      })
    } else {
      if (localStorage.roleId == 1) {
        this.$router.replace({ name: "dashboard" });
      }

      this.OPEN_SESSION_LIST().catch(() => { });
      this.getCoPropList().then(() => this.applyRoutePrefill());
    }
  },
  beforeDestroy() {
    this.vilogiRefreshTimers.forEach(clearTimeout);
    document.body.classList.remove('modal-open');
  }
}
</script>
<style scoped>
.font-size a {
  width: 25px;
}

.vilogi-modal-body {
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
  text-align: left;
}

.vilogi-blocked-alert {
  min-width: 0;
  max-width: 100%;
  overflow-wrap: break-word;
  word-break: break-word;
  white-space: normal;
}

.void-text {
  text-decoration: line-through;
  opacity: 0.7;
}

.vilogi-badge-lg {
  font-size: 0.7rem;
}
</style>



// WEBPACK FOOTER //
// src/pages/Admin/completedSession.vue